import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import BlockIcon from '@mui/icons-material/Block';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { startOfWeek, endOfWeek, addDays, format, isToday, isSameDay, addWeeks, subWeeks, parseISO } from 'date-fns';
import { getAppointments, getUnavailableBlocks, getPractitioners } from '../../api/calendarService';
import { enAU } from 'date-fns/locale';
import { CalendarEvent } from '../../models/calendar';
import { AnimatedBeam } from '../ui/AnimatedBeam';
import { useTimezone } from '../../context/TimezoneContext';
import { convertToTimezone, convertFromTimezone } from '../../utils/timezone';

// Time constants
const START_HOUR = 8; // 8:00 AM
const END_HOUR = 21;  // 9:00 PM
const HOUR_HEIGHT = 60; // pixels per hour
const TIME_SLOT_HEIGHT = HOUR_HEIGHT / 4; // 15-minute slots

interface PractitionerWeekViewProps {
  onClose: () => void;
  practitionerId?: string;
  onCalendarUpdated: () => void;
  initialDate?: Date;
  onEventClick?: (event: CalendarEvent) => void;
  onTimeSlotClick?: (date: Date, practitionerId?: string) => void;
}

// Add an interface to define the ref methods
export interface PractitionerWeekViewRef {
  removeUnavailableBlock: (blockId: string) => void;
  markAppointmentCancelled: (appointmentId: string) => void;
}

const PractitionerWeekView = forwardRef<PractitionerWeekViewRef, PractitionerWeekViewProps>(({
  onClose,
  practitionerId,
  onCalendarUpdated,
  initialDate,
  onEventClick,
  onTimeSlotClick
}, ref) => {
  // Create refs for practitioners
  const practitionerRefs = useRef<{[id: string]: React.RefObject<HTMLDivElement>}>({});
  const theme = useTheme();
  const { selectedTimezone, practitionerTimezones, getPractitionerTimezone } = useTimezone();
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || new Date());
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(currentDate, { weekStartsOn: 0 }));
  const [weekDays, setWeekDays] = useState<Date[]>([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  
  // View options
  const [showCancelled, setShowCancelled] = useState<boolean>(false);
  
  // Data for calendar
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [allAppointments, setAllAppointments] = useState<CalendarEvent[]>([]);
  const [appointments, setAppointments] = useState<CalendarEvent[]>([]);
  const [unavailableBlocks, setUnavailableBlocks] = useState<CalendarEvent[]>([]);
  
  // Function to remove a deleted block from the UI immediately
  const removeUnavailableBlock = (blockId: string) => {
    console.log(`Removing unavailable block ${blockId} from week view`);
    setUnavailableBlocks(prevBlocks => 
      prevBlocks.filter(block => block.id !== blockId)
    );
  };
  
  // Function to mark an appointment as cancelled immediately
  const markAppointmentCancelled = (appointmentId: string) => {
    console.log(`Marking appointment ${appointmentId} as cancelled in week view`);
    // Update the appointment in both state collections
    const updateAppointment = (appts: CalendarEvent[]) => 
      appts.map(appt => {
        if (appt.id === appointmentId) {
          return {
            ...appt,
            cancelled: true,
            color: '#9e9e9e', // Grey for cancelled appointments
            status: 'cancelled'
          };
        }
        return appt;
      });
    
    setAppointments(updateAppointment);
    setAllAppointments(updateAppointment);
  };
  
  // Expose functions through the ref
  useImperativeHandle(ref, () => ({
    removeUnavailableBlock,
    markAppointmentCancelled
  }));

  // Reference for the time grid
  const timeGridRef = useRef<HTMLDivElement>(null);

  // Fetch practitioners
  const fetchPractitioners = async () => {
    try {
      setIsLoading(true);
      const practitionersResponse = await getPractitioners();
      
      let formattedPractitioners = [];
      if (practitionersResponse.data && practitionersResponse.data.practitioners) {
        formattedPractitioners = practitionersResponse.data.practitioners.map((p: any) => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`
        }));
        setPractitioners(formattedPractitioners);
      } else if (practitionersResponse.practitioners) {
        formattedPractitioners = practitionersResponse.practitioners.map((p: any) => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`
        }));
        setPractitioners(formattedPractitioners);
      }
      
      // Create refs for each practitioner
      const refs: {[id: string]: React.RefObject<HTMLDivElement>} = {};
      formattedPractitioners.forEach((p) => {
        if (p.id) {
          refs[p.id] = React.createRef<HTMLDivElement>();
        }
      });
      practitionerRefs.current = refs;
    } catch (err) {
      console.error('Error fetching practitioners:', err);
      setError('Failed to load practitioners');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch calendar data (appointments and unavailable blocks)
  const fetchCalendarData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Calculate the date range for the current week
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      
      // Format dates for API
      const startDate = start.toISOString();
      const endDate = end.toISOString();
      
      setMessage(`Fetching calendar data for ${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}...`);
      setShowMessage(true);

      console.log('Fetching calendar data with date range:', { startDate, endDate });

      // Fetch appointments and unavailable blocks
      const [appointmentsResponse, unavailableBlocksResponse] = await Promise.all([
        getAppointments({
          startDate,
          endDate
        }),
        getUnavailableBlocks({
          startDate,
          endDate
        })
      ]);

      console.log('Raw appointment data:', appointmentsResponse);
      console.log('Raw unavailable blocks data:', unavailableBlocksResponse);

      console.log('Raw appointmentsResponse object:', appointmentsResponse);
      console.log('appointmentsResponse structure:', Object.keys(appointmentsResponse));
      
      // Check if we have appointments in expected format
      if (!appointmentsResponse.appointments) {
        console.error('No appointments property found in the response');
        console.log('Checking for other possible structures...');
        
        // Try to find appointments in alternative structures
        let appointments = null;
        
        if (Array.isArray(appointmentsResponse)) {
          console.log('Response is an array, using it directly');
          appointments = appointmentsResponse;
        } else if (appointmentsResponse.data && appointmentsResponse.data.appointments) {
          console.log('Found appointments in response.data.appointments');
          appointments = appointmentsResponse.data.appointments;
        } else if (appointmentsResponse.data && Array.isArray(appointmentsResponse.data)) {
          console.log('Found array in response.data');
          appointments = appointmentsResponse.data;
        } else {
          console.error('Could not find appointments in the response');
          console.log('Full response:', JSON.stringify(appointmentsResponse));
        }
        
        // Use found appointments if any, otherwise empty array
        appointmentsResponse.appointments = appointments || [];
      }
      
      // Log appointments count
      console.log(`Found ${appointmentsResponse.appointments.length} appointments in response`);
      
      // Process appointments to calendar events
      const allAppointmentEvents = appointmentsResponse.appointments?.map(appt => {
        try {
          // Log each appointment for debugging
          console.log('Processing appointment:', {
            id: appt.id,
            patient: appt.patient_name,
            start: appt.appointment_start || appt.starts_at,
            end: appt.appointment_end || appt.ends_at,
            cancelled: !!appt.cancellation_time,
            cancellationTime: appt.cancellation_time,
            practitioner: appt.practitioner_name,
            practitionerId: appt.practitioner_id
          });
          
          // Ensure appointment has valid start and end times
          const startTime = appt.appointment_start || appt.starts_at;
          const endTime = appt.appointment_end || appt.ends_at;
          
          if (!startTime || !endTime) {
            console.error('Appointment missing start or end time:', appt);
            return null;
          }
          
          // Get practitioner timezone and convert if needed
          const practitionerTimezone = appt.practitioner_timezone || getPractitionerTimezone(appt.practitioner_id) || 'Australia/Adelaide';
          let startDate = new Date(startTime);
          let endDate = new Date(endTime);
          
          // Convert timezone if needed
          if (practitionerTimezone !== selectedTimezone) {
            console.log(`🔄 Converting appointment ${appt.id} from ${practitionerTimezone} to ${selectedTimezone}`);
            
            startDate = convertToTimezone(
              convertFromTimezone(startDate, practitionerTimezone),
              selectedTimezone
            );
            
            endDate = convertToTimezone(
              convertFromTimezone(endDate, practitionerTimezone),
              selectedTimezone
            );
            
            console.log(`  Original: ${startTime}, Converted: ${startDate.toISOString()}`);
          }
          
          return {
            id: appt.id,
            title: appt.patient_name || 'Appointment',
            start: startDate,
            end: endDate,
            type: 'appointment',
            practitionerId: appt.practitioner_id,
            practitionerName: appt.practitioner_name,
            patientName: appt.patient_name,
            appointmentType: appt.appointment_type_name,
            cancelled: appt.cancellation_time != null,
            cancellationTime: appt.cancellation_time,
            color: appt.practitioner_id ? null : '#f44336' // Color will be set based on practitioner
          };
        } catch (err) {
          console.error('Error processing appointment:', err, appt);
          return null;
        }
      }).filter(Boolean) || [];
      
      // Store all appointments including cancelled ones
      setAllAppointments(allAppointmentEvents);
      
      // Filter based on the showCancelled flag
      const appointmentEvents = showCancelled 
        ? allAppointmentEvents 
        : allAppointmentEvents.filter(appt => !appt.cancelled);
      
      console.log(`Filtered out ${allAppointmentEvents.length - appointmentEvents.length} cancelled appointments`);
      console.log('Cancelled appointments:', allAppointmentEvents.filter(appt => appt.cancelled));

      console.log('Processing unavailable blocks response:', unavailableBlocksResponse);
      
      // Check if we have unavailable blocks in expected format
      if (!unavailableBlocksResponse.unavailable_blocks) {
        console.error('No unavailable_blocks property found in the response');
        console.log('Checking for other possible structures...');
        
        // Try to find unavailable blocks in alternative structures
        let blocks = null;
        
        if (Array.isArray(unavailableBlocksResponse)) {
          console.log('Response is an array, using it directly');
          blocks = unavailableBlocksResponse;
        } else if (unavailableBlocksResponse.data && unavailableBlocksResponse.data.unavailable_blocks) {
          console.log('Found unavailable blocks in response.data.unavailable_blocks');
          blocks = unavailableBlocksResponse.data.unavailable_blocks;
        } else if (unavailableBlocksResponse.data && Array.isArray(unavailableBlocksResponse.data)) {
          console.log('Found array in response.data');
          blocks = unavailableBlocksResponse.data;
        } else {
          console.error('Could not find unavailable blocks in the response');
          console.log('Full response:', JSON.stringify(unavailableBlocksResponse));
        }
        
        // Use found blocks if any, otherwise empty array
        unavailableBlocksResponse.unavailable_blocks = blocks || [];
      }
      
      // Log blocks count
      console.log(`Found ${unavailableBlocksResponse.unavailable_blocks.length} unavailable blocks in response`);
      
      // Process unavailable blocks to calendar events
      const unavailableBlockEvents = unavailableBlocksResponse.unavailable_blocks?.map(block => {
        try {
          console.log('Processing unavailable block:', block);
          
          // Ensure block has valid start and end times
          if (!block.starts_at || !block.ends_at) {
            console.error('Block missing start or end time:', block);
            return null;
          }
          
          // Get practitioner timezone and convert if needed
          const practitionerTimezone = block.practitioner_timezone || getPractitionerTimezone(block.practitioner_id) || 'Australia/Adelaide';
          let startDate = new Date(block.starts_at);
          let endDate = new Date(block.ends_at);
          
          // Convert timezone if needed
          if (practitionerTimezone !== selectedTimezone) {
            console.log(`🔄 Converting unavailable block ${block.id} from ${practitionerTimezone} to ${selectedTimezone}`);
            
            startDate = convertToTimezone(
              convertFromTimezone(startDate, practitionerTimezone),
              selectedTimezone
            );
            
            endDate = convertToTimezone(
              convertFromTimezone(endDate, practitionerTimezone),
              selectedTimezone
            );
            
            console.log(`  Original: ${block.starts_at}, Converted: ${startDate.toISOString()}`);
          }
          
          return {
            id: block.id,
            title: 'Unavailable',
            start: startDate,
            end: endDate,
            type: 'unavailable',
            practitionerId: block.practitioner_id,
            practitionerName: block.practitioner_name,
            notes: block.notes,
            color: '#f44336'
          };
        } catch (err) {
          console.error('Error processing unavailable block:', err, block);
          return null;
        }
      }).filter(Boolean) || [];

      console.log('Processed appointments:', appointmentEvents.length);
      console.log('Processed unavailable blocks:', unavailableBlockEvents.length);

      setAppointments(appointmentEvents);
      setUnavailableBlocks(unavailableBlockEvents);

      setMessage(`Loaded ${appointmentEvents.length} appointments and ${unavailableBlockEvents.length} unavailable blocks`);
      setShowMessage(true);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize the week days and fetch data
  useEffect(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    setWeekStart(start);
    
    // Generate array of days for the week
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    setWeekDays(days);

    // Fetch practitioners and calendar data
    fetchPractitioners();
    fetchCalendarData();
  }, [currentDate, selectedTimezone]);

  // Navigate to previous week
  const handlePrevWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  // Navigate to next week
  const handleNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  // Navigate to current week
  const handleCurrentWeek = () => {
    setCurrentDate(new Date());
  };

  // Format time for display
  const formatTime = (hour: number, minute: number): string => {
    const hourDisplay = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hourDisplay}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  // Get the grid template columns based on the number of practitioners
  const getGridTemplateColumns = () => {
    const practitionerCount = practitioners.length || 1;
    // Create a simpler layout with precise column widths
    return `70px repeat(${7 * practitionerCount}, 1fr)`;
  };

  // Check if a time slot has an appointment
  const getAppointmentsForTimeSlot = (day: Date, hour: number, minute: number, practitionerId: string) => {
    try {
      // Create the time slot start and end times
      const slotStart = new Date(day);
      slotStart.setHours(hour, minute, 0, 0);
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + 15);
      
      // Debug information
      if (hour === 9 && minute === 0) { // Only log for the 9 AM slot to avoid excessive logging
        console.log(`Checking appointments for ${day.toLocaleDateString()} ${hour}:${minute.toString().padStart(2, '0')} for practitioner ${practitionerId}`);
        console.log(`Total appointments available: ${appointments.length}`);
        
        // Check if any appointments exist for this practitioner
        const practitionerAppts = appointments.filter(a => a.practitionerId === practitionerId);
        console.log(`Appointments for practitioner ${practitionerId}: ${practitionerAppts.length}`);
        
        if (practitionerAppts.length > 0) {
          console.log('First appointment for this practitioner:', {
            id: practitionerAppts[0].id,
            start: practitionerAppts[0].start,
            end: practitionerAppts[0].end,
            patientName: practitionerAppts[0].patientName,
            practId: practitionerAppts[0].practitionerId,
            appointmentTypeName: practitionerAppts[0].appointmentTypeName
          });
        }
      }
      
      return appointments.filter(appointment => {
        try {
          // For appointments, we need to check if the appointment start time falls within this slot
          // (We only want to show the appointment at its start time slot)
          const appointmentStart = appointment.start;
          
          if (!appointmentStart || !(appointmentStart instanceof Date)) {
            console.error('Invalid appointment start date:', appointment);
            return false;
          }
          
          const isSameDay = 
            appointmentStart.getDate() === day.getDate() &&
            appointmentStart.getMonth() === day.getMonth() &&
            appointmentStart.getFullYear() === day.getFullYear();
          
          // We only want to render the appointment at EXACTLY its start time slot
          // This prevents duplicate renderings across multiple slots
          const isExactStartTimeSlot = 
            appointmentStart.getHours() === hour &&
            Math.floor(appointmentStart.getMinutes() / 15) * 15 === minute;
          
          // Convert practitioner IDs to strings for comparison
          const appPractId = String(appointment.practitionerId || '');
          const slotPractId = String(practitionerId || '');
          
          // Debug any matching appointments
          if (isSameDay && isExactStartTimeSlot && appPractId === slotPractId) {
            console.log(`Found appointment for ${day.toLocaleDateString()} at ${hour}:${minute.toString().padStart(2, '0')}`);
            console.log('Appointment details:', {
              id: appointment.id,
              patientName: appointment.patientName,
              appointmentTypeName: appointment.appointmentTypeName,
              start: appointmentStart.toLocaleString(),
              end: appointment.end ? appointment.end.toLocaleString() : 'Unknown',
              practId: appPractId,
              duration: appointment.end ? 
                `${Math.ceil((appointment.end.getTime() - appointmentStart.getTime()) / (60 * 1000))} minutes` : 
                'Unknown'
            });
          }
          
          return (
            isSameDay && 
            isExactStartTimeSlot && 
            appPractId === slotPractId
          );
        } catch (err) {
          console.error('Error processing appointment in filter:', err, appointment);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in getAppointmentsForTimeSlot:', err);
      return [];
    }
  };

  // Check if a time slot has an unavailable block
  const getUnavailableBlocksForTimeSlot = (day: Date, hour: number, minute: number, practitionerId: string) => {
    try {
      // Create the time slot start and end times
      const slotStart = new Date(day);
      slotStart.setHours(hour, minute, 0, 0);
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + 15);
      
      // Debug information for one specific time slot
      if (hour === 9 && minute === 0) {
        console.log(`Checking unavailable blocks for ${day.toLocaleDateString()} ${hour}:${minute.toString().padStart(2, '0')} for practitioner ${practitionerId}`);
        console.log(`Total unavailable blocks available: ${unavailableBlocks.length}`);
        
        // Check if any blocks exist for this practitioner
        const practitionerBlocks = unavailableBlocks.filter(b => b.practitionerId === practitionerId);
        console.log(`Unavailable blocks for practitioner ${practitionerId}: ${practitionerBlocks.length}`);
        
        if (practitionerBlocks.length > 0) {
          console.log('First unavailable block for this practitioner:', {
            id: practitionerBlocks[0].id,
            start: practitionerBlocks[0].start,
            end: practitionerBlocks[0].end,
            practId: practitionerBlocks[0].practitionerId
          });
        }
      }
      
      return unavailableBlocks.filter(block => {
        try {
          // For unavailable blocks, we only want to show them at their start time slot
          const blockStart = block.start;
          const blockEnd = block.end;
          
          if (!blockStart || !(blockStart instanceof Date) || 
              !blockEnd || !(blockEnd instanceof Date)) {
            console.error('Invalid block start or end date:', block);
            return false;
          }
          
          const isSameDay = 
            blockStart.getDate() === day.getDate() &&
            blockStart.getMonth() === day.getMonth() &&
            blockStart.getFullYear() === day.getFullYear();
          
          // We only want to render the block at EXACTLY its start time slot
          // This prevents duplicate renderings across multiple slots
          const isExactStartTimeSlot = 
            blockStart.getHours() === hour &&
            Math.floor(blockStart.getMinutes() / 15) * 15 === minute;
          
          // Convert practitioner IDs to strings for comparison
          const blockPractId = String(block.practitionerId || '');
          const slotPractId = String(practitionerId || '');
          
          // Debug any matching blocks
          if (isSameDay && isExactStartTimeSlot && blockPractId === slotPractId) {
            // Important: For debugging, log with all time details
            const startMinutes = blockStart.getHours() * 60 + blockStart.getMinutes();
            const endMinutes = blockEnd.getHours() * 60 + blockEnd.getMinutes();
            const durationMinutes = endMinutes - startMinutes;
            const quarterHours = durationMinutes / 15;
            
            console.log(`Found unavailable block for ${day.toLocaleDateString()} at ${hour}:${minute.toString().padStart(2, '0')}`, {
              id: block.id,
              start: blockStart.toLocaleString(),
              startHour: blockStart.getHours(),
              startMinute: blockStart.getMinutes(),
              end: blockEnd.toLocaleString(),
              endHour: blockEnd.getHours(),
              endMinute: blockEnd.getMinutes(),
              practId: blockPractId,
              durationMinutes,
              quarterHours,
              exactSlots: Math.floor(quarterHours)
            });
            
            // Manually set the end time to an exact 15-minute boundary
            // This is a workaround for the display issue
            const adjustedBlock = { ...block };
            
            // Adjust the end time to be exactly on a 15-minute boundary
            const endHour = blockEnd.getHours();
            const endMinute = Math.floor(blockEnd.getMinutes() / 15) * 15;
            
            const adjustedEndTime = new Date(blockEnd);
            adjustedEndTime.setHours(endHour, endMinute, 0, 0);
            adjustedBlock.end = adjustedEndTime;
            
            // Update the block with our adjusted end time
            block.adjustedEnd = adjustedEndTime;
            console.log('Adjusted end time to exact 15-minute boundary:', adjustedEndTime.toLocaleTimeString());
          }
          
          return (
            isSameDay && 
            isExactStartTimeSlot && 
            blockPractId === slotPractId
          );
        } catch (err) {
          console.error('Error processing unavailable block in filter:', err, block);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in getUnavailableBlocksForTimeSlot:', err);
      return [];
    }
  };

  // Add debug info
  useEffect(() => {
    if (appointments.length > 0 || unavailableBlocks.length > 0) {
      console.log('Current appointments:', appointments);
      console.log('Current unavailable blocks:', unavailableBlocks);
    }
  }, [appointments, unavailableBlocks]);
  
  // Update appointments when showCancelled changes
  useEffect(() => {
    if (allAppointments.length > 0) {
      const filteredAppointments = showCancelled 
        ? allAppointments 
        : allAppointments.filter(appt => !appt.cancelled);
      
      setAppointments(filteredAppointments);
      console.log(`Filter changed: ${showCancelled ? 'Showing' : 'Hiding'} cancelled appointments`);
      console.log(`Displaying ${filteredAppointments.length} of ${allAppointments.length} appointments`);
    }
  }, [showCancelled, allAppointments]);

  return (
    <Paper
      elevation={4}
      sx={{
        p: 2,
        borderRadius: 2,
        margin: '0 auto',
        overflow: 'hidden',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'white',
        maxWidth: '100%'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center">
          <EventIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            Week View
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<TodayIcon />}
            onClick={handleCurrentWeek}
            variant="outlined"
            size="small"
          >
            Current Week
          </Button>
          <IconButton onClick={handlePrevWeek} size="small">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleNextWeek} size="small">
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={fetchCalendarData} 
            size="small"
            color="primary"
            disabled={isLoading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </Box>
      </Box>
      
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        {format(weekStart, 'MMMM d', { locale: enAU })} - {format(addDays(weekStart, 6), 'MMMM d, yyyy', { locale: enAU })}
        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
          ({selectedTimezone.replace('Australia/', '')})
        </Typography>
      </Typography>
      
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading calendar data...
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        height: '600px',
        maxWidth: '100%'
      }}>
        {/* Week header with practitioners */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: getGridTemplateColumns(),
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          position: 'sticky',
          top: 0,
          zIndex: 2
        }}>
          {/* Empty cell for time column */}
          <Box sx={{ 
            p: 1, 
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'primary.dark',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0))'
          }}>
            <Typography 
              variant="subtitle2" 
              fontWeight="bold"
              sx={{
                color: 'white',
                textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                fontSize: '0.9rem',
                letterSpacing: '1px'
              }}
            >
              TIME
            </Typography>
          </Box>
          
          {/* Day columns with practitioner sub-columns */}
          {weekDays.map((day, dayIndex) => (
            <React.Fragment key={`day-header-${dayIndex}`}>
              {/* Day header that spans all practitioners */}
              <Box 
                sx={{ 
                  gridColumn: `span ${practitioners.length || 1}`,
                  textAlign: 'center',
                  p: 1,
                  borderRight: dayIndex < 6 ? '1px solid' : 'none',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: isToday(day) ? 'secondary.main' : 'primary.main',
                  boxShadow: isToday(day) ? '0 0 5px rgba(255,255,255,0.3) inset' : 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isToday(day) && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                    backgroundSize: '4px 4px',
                    zIndex: 0
                  }} />
                )}
                <Typography 
                  variant="subtitle1" 
                  fontWeight="bold"
                  sx={{ 
                    position: 'relative',
                    zIndex: 1,
                    color: 'white',
                    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                    fontSize: '1rem'
                  }}
                >
                  {format(day, 'EEE, d MMM', { locale: enAU })}
                </Typography>
              </Box>
            </React.Fragment>
          ))}

          {/* Practitioner headers under each day */}
          <Box sx={{ 
            gridColumn: '1',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'primary.dark',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}></Box>

          {weekDays.map((day, dayIndex) => (
            practitioners.length > 0 ? (
              practitioners.map((practitioner, practitionerIndex) => (
                <Box 
                  key={`practitioner-${dayIndex}-${practitionerIndex}`}
                  ref={dayIndex === 0 ? practitionerRefs.current[practitioner.id] : undefined}
                  sx={{ 
                    p: 1,
                    textAlign: 'center',
                    borderRight: (dayIndex < 6 || practitionerIndex < practitioners.length - 1) ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: 'primary.dark',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.15)',
                      zIndex: 0
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    sx={{ 
                      position: 'relative', 
                      zIndex: 1,
                      color: 'white',
                      textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                      fontSize: '0.9rem'
                    }}
                  >
                    {practitioner.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box 
                key={`no-practitioners-${dayIndex}`}
                sx={{ 
                  p: 1,
                  textAlign: 'center',
                  borderRight: dayIndex < 6 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: 'primary.dark'
                }}
              >
                <Typography 
                  variant="body2" 
                  fontWeight="bold"
                  sx={{ 
                    color: 'white',
                    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.5)',
                    fontSize: '0.9rem'
                  }}
                >
                  No practitioners
                </Typography>
              </Box>
            )
          ))}
        </Box>
        
        {/* Time grid */}
        <Box 
          ref={timeGridRef}
          sx={{ 
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(),
            gridTemplateRows: `repeat(${(END_HOUR - START_HOUR) * 4}, ${TIME_SLOT_HEIGHT}px)`,
            position: 'relative',
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'auto'
          }}
        >
          {/* Time labels */}
          {Array.from({ length: END_HOUR - START_HOUR }).map((_, i) => (
            <React.Fragment key={`hour-${i}`}>
              <Box sx={{ 
                gridColumn: '1',
                gridRow: `${i * 4 + 1} / span 4`,
                borderRight: '1px solid',
                borderBottom: i < END_HOUR - START_HOUR - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                position: 'sticky',
                left: 0,
                zIndex: 1
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    transform: 'translateY(-50%)',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    padding: '3px 5px',
                    borderRadius: '3px',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {formatTime(i + START_HOUR, 0)}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
          
          {/* Time slots grid for each practitioner for each day */}
          {weekDays.map((day, dayIndex) => (
            practitioners.length > 0 ? (
              practitioners.map((practitioner, practitionerIndex) => (
                Array.from({ length: (END_HOUR - START_HOUR) * 4 }).map((_, slotIndex) => {
                  const hour = Math.floor(slotIndex / 4) + START_HOUR;
                  const minute = (slotIndex % 4) * 15;
                  const isHourStart = slotIndex % 4 === 0;
                  const isHalfHour = slotIndex % 4 === 2;
                  
                  // Check for appointments and unavailable blocks in this slot
                  const appointmentsInSlot = getAppointmentsForTimeSlot(day, hour, minute, practitioner.id);
                  const blocksInSlot = getUnavailableBlocksForTimeSlot(day, hour, minute, practitioner.id);
                  const hasAppointment = appointmentsInSlot.length > 0;
                  const isUnavailable = blocksInSlot.length > 0;
                  
                  return (
                    <Box
                      key={`slot-${dayIndex}-${practitionerIndex}-${slotIndex}`}
                      sx={{
                        gridColumn: `${dayIndex * practitioners.length + practitionerIndex + 2}`,
                        gridRow: slotIndex + 1,
                        borderRight: (dayIndex < 6 || practitionerIndex < practitioners.length - 1) ? '1px solid' : 'none',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: isUnavailable 
                          ? 'rgba(244, 67, 54, 0.15)' 
                          : hasAppointment 
                            ? 'rgba(33, 150, 243, 0.15)'
                            : isHourStart 
                              ? 'rgba(0, 0, 0, 0.03)' 
                              : isHalfHour 
                                ? 'rgba(0, 0, 0, 0.01)' 
                                : 'background.paper',
                        height: TIME_SLOT_HEIGHT,
                        position: 'relative',
                        cursor: (!hasAppointment && !isUnavailable) ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: isUnavailable 
                            ? 'rgba(244, 67, 54, 0.25)' 
                            : hasAppointment 
                              ? 'rgba(33, 150, 243, 0.25)'
                              : 'rgba(0, 0, 0, 0.05)',
                        }
                      }}
                      onClick={(e) => {
                        // Only handle click for empty slots
                        if (!hasAppointment && !isUnavailable && onTimeSlotClick) {
                          e.stopPropagation();
                          // Create a date object for this time slot
                          const slotDate = new Date(day);
                          slotDate.setHours(hour, minute, 0, 0);
                          // Pass the practitioner.id to automatically select the practitioner
                          // based on which column was clicked
                          onTimeSlotClick(slotDate, practitioner.id);
                        }
                      }}
                    >
                      {/* Render appointments */}
                      {hasAppointment && (
                        <Tooltip
                          title={
                            (() => {
                              const appt = appointmentsInSlot[0];
                              const startTime = appt.start;
                              const endTime = appt.end;
                              let durationText = '';
                              
                              // Calculate duration if both start and end times exist
                              if (startTime && endTime) {
                                const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (60 * 1000));
                                durationText = ` (${durationMinutes} mins)`;
                              }
                              
                              return `${appt.patientName || 'Appointment'} - ${appt.appointmentTypeName || ''}${durationText} - ${formatTime(hour, minute)}`;
                            })()
                          }
                          arrow
                        >
                          <Box 
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              // Calculate height based on appointment duration
                              height: (() => {
                                const startTime = appointmentsInSlot[0].start;
                                const endTime = appointmentsInSlot[0].end;
                                if (!startTime || !endTime) return '100%';
                                
                                // Calculate exact slots needed (handling partial 15-min slots)
                                const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
                                const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
                                
                                // Calculate exact quarter-hours (completely dropping any remainder)
                                const exactQuarterHours = Math.max(0, Math.floor((endMinutes - startMinutes) / 15));
                                
                                console.log(`Appointment from ${startTime.toLocaleTimeString()} to ${endTime.toLocaleTimeString()}: ${exactQuarterHours} slots (${exactQuarterHours * 15} minutes)`);
                                
                                // Return height as number of exact quarter-hours
                                return `${exactQuarterHours * TIME_SLOT_HEIGHT}px`;
                              })(),
                              zIndex: 2, // Higher z-index to show on top
                              bgcolor: appointmentsInSlot[0].cancelled 
                                ? 'rgba(158, 158, 158, 0.3)'
                                : appointmentsInSlot[0].color 
                                  ? `${appointmentsInSlot[0].color}40`
                                  : 'rgba(33, 150, 243, 0.3)',
                              backgroundImage: appointmentsInSlot[0].cancelled 
                                ? 'none'
                                : 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent)',
                              backgroundSize: '10px 10px',
                              borderLeft: appointmentsInSlot[0].cancelled
                                ? '3px solid #9e9e9e'
                                : appointmentsInSlot[0].color
                                  ? `3px solid ${appointmentsInSlot[0].color}`
                                  : '3px solid #2196f3',
                              display: 'flex',
                              alignItems: 'flex-start',
                              padding: '4px',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              '&:hover': {
                                filter: 'brightness(1.1)',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                transform: 'scale(1.01)'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onEventClick) {
                                console.log('Appointment clicked:', appointmentsInSlot[0]);
                                onEventClick(appointmentsInSlot[0]);
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <EventIcon 
                                  sx={{ 
                                    fontSize: '0.8rem', 
                                    mr: 0.5,
                                    color: appointmentsInSlot[0].cancelled 
                                      ? theme.palette.text.disabled
                                      : theme.palette.primary.main
                                  }} 
                                />
                                <Typography variant="caption" sx={{
                                  fontWeight: 'medium',
                                  color: appointmentsInSlot[0].cancelled
                                    ? theme.palette.text.disabled
                                    : 'inherit'
                                }}>
                                  {appointmentsInSlot[0].patientName || 'Appointment'}
                                </Typography>
                              </Box>
                              
                              {/* Add time range */}
                              {appointmentsInSlot[0].start && appointmentsInSlot[0].end && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 'medium',
                                  color: appointmentsInSlot[0].cancelled
                                    ? theme.palette.text.disabled
                                    : theme.palette.primary.dark,
                                  ml: 2.3
                                }}>
                                  {format(appointmentsInSlot[0].start, 'h:mm a')} - {format(appointmentsInSlot[0].end, 'h:mm a')}
                                </Typography>
                              )}
                              
                              {/* Add appointment type if available */}
                              {appointmentsInSlot[0].appointmentTypeName && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.65rem',
                                  color: appointmentsInSlot[0].cancelled
                                    ? theme.palette.text.disabled
                                    : 'text.secondary',
                                  ml: 2.3,
                                  mt: 0.5
                                }}>
                                  {appointmentsInSlot[0].appointmentTypeName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Tooltip>
                      )}
                      
                      {/* Render unavailable blocks */}
                      {isUnavailable && !hasAppointment && (
                        <Tooltip
                          title={
                            (() => {
                              const block = blocksInSlot[0];
                              const startTime = block?.start;
                              // Use adjusted end time if available
                              const endTime = block?.adjustedEnd || block?.end;
                              let durationText = '';
                              
                              // Calculate duration if both start and end times exist
                              if (startTime && endTime) {
                                // Calculate minutes between times
                                const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
                                const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
                                const durationMinutes = endMinutes - startMinutes;
                                durationText = ` (${durationMinutes} mins)`;
                              }
                              
                              return `Unavailable${durationText} - ${formatTime(hour, minute)}${block?.notes ? ` - ${block.notes}` : ''}`;
                            })()
                          }
                          arrow
                        >
                          <Box 
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              // Calculate height based on block duration
                              height: (() => {
                                const block = blocksInSlot[0];
                                if (!block || !block.start || !block.end) return '100%';
                                
                                // Use the adjusted end time if available, otherwise use the original end time
                                const startTime = block.start;
                                // Use adjustedEnd if it exists, otherwise fall back to original end
                                const endTime = block.adjustedEnd || block.end;
                                
                                // Calculate exact slots needed (handling partial 15-min slots)
                                const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
                                const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
                                
                                // Important: For unavailable blocks, ensure we exactly match the 15-minute boundaries
                                // Calculate the number of complete 15-minute slots
                                const exactSlots = Math.floor((endMinutes - startMinutes) / 15);
                                
                                console.log(`RENDERING Unavailable block from ${startTime.toLocaleTimeString()} to ${endTime.toLocaleTimeString()}`, {
                                  originalEnd: block.end.toLocaleTimeString(),
                                  adjustedEnd: block.adjustedEnd ? block.adjustedEnd.toLocaleTimeString() : 'none',
                                  startMinutes,
                                  endMinutes,
                                  duration: endMinutes - startMinutes,
                                  exactSlots,
                                  height: `${exactSlots * TIME_SLOT_HEIGHT}px`
                                });
                                
                                // Return height as number of exact 15-minute slots
                                return `${exactSlots * TIME_SLOT_HEIGHT}px`;
                              })(),
                              zIndex: 2, // Higher z-index to show on top
                              bgcolor: 'rgba(244, 67, 54, 0.3)',
                              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 10px)',
                              backgroundSize: '10px 10px',
                              borderLeft: '3px solid rgba(244, 67, 54, 0.8)',
                              display: 'flex',
                              alignItems: 'flex-start',
                              padding: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              '&:hover': {
                                filter: 'brightness(1.1)',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                transform: 'scale(1.01)'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onEventClick && blocksInSlot.length > 0) {
                                console.log('Unavailable block clicked:', blocksInSlot[0]);
                                onEventClick(blocksInSlot[0]);
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <BlockIcon 
                                  sx={{ 
                                    fontSize: '0.8rem', 
                                    mr: 0.5,
                                    color: theme.palette.error.main
                                  }} 
                                />
                                <Typography variant="caption" sx={{
                                  fontWeight: 'medium'
                                }}>
                                  Unavailable
                                </Typography>
                              </Box>
                              
                              {/* Show time range */}
                              {blocksInSlot[0]?.start && (blocksInSlot[0]?.adjustedEnd || blocksInSlot[0]?.end) && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 'medium',
                                  color: theme.palette.error.dark,
                                  ml: 2.3
                                }}>
                                  {format(blocksInSlot[0].start, 'h:mm a')} - {format(blocksInSlot[0].adjustedEnd || blocksInSlot[0].end, 'h:mm a')}
                                </Typography>
                              )}
                              
                              {/* Add block notes if available */}
                              {blocksInSlot[0]?.notes && (
                                <Typography variant="caption" sx={{
                                  fontSize: '0.65rem',
                                  color: 'text.secondary',
                                  ml: 2.3,
                                  mt: 0.5,
                                  fontStyle: 'italic'
                                }}>
                                  {blocksInSlot[0].notes}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })
              ))
            ) : (
              // Placeholder when no practitioners are available
              Array.from({ length: (END_HOUR - START_HOUR) * 4 }).map((_, slotIndex) => (
                <Box
                  key={`no-practitioner-slot-${dayIndex}-${slotIndex}`}
                  sx={{
                    borderRight: dayIndex < 6 ? '1px solid' : 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    height: TIME_SLOT_HEIGHT
                  }}
                />
              ))
            )
          ))}
        </Box>
      </Box>

      {/* AnimatedBeams to connect practitioners */}
      <Box sx={{ position: 'relative', mt: 3 }}>
        {practitioners.length > 1 && practitioners.map((practitioner, index) => {
          // Skip last practitioner as we connect each practitioner with the next one
          if (index === practitioners.length - 1) return null;
          
          // Get refs for current and next practitioner
          const fromRef = practitionerRefs.current[practitioner.id];
          const toRef = practitionerRefs.current[practitioners[index + 1].id];
          
          // Only create beams if we have valid refs
          if (fromRef && toRef) {
            return (
              <AnimatedBeam
                key={`beam-${practitioner.id}-${practitioners[index + 1].id}`}
                containerRef={timeGridRef}
                fromRef={fromRef}
                toRef={toRef}
                curvature={-20}
                gradientStartColor={theme.palette.primary.main}
                gradientStopColor={theme.palette.secondary.main}
                duration={3 + index}
                pathWidth={2}
                pathOpacity={0.15}
              />
            );
          }
          return null;
        })}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {practitioners.length > 0 
              ? `Showing ${practitioners.length} practitioners` 
              : 'No practitioners available'}
          </Typography>
          
          <Button
            variant={showCancelled ? "contained" : "outlined"}
            color={showCancelled ? "secondary" : "primary"}
            size="small"
            onClick={() => setShowCancelled(!showCancelled)}
            startIcon={showCancelled ? <VisibilityIcon /> : <VisibilityOffIcon />}
            endIcon={<CancelIcon />}
            sx={{ mr: 2 }}
          >
            {showCancelled ? "Showing Cancelled" : "Hiding Cancelled"}
          </Button>
        </Box>
        
        <Button 
          variant="contained" 
          onClick={fetchCalendarData}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh View'}
        </Button>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar
        open={showMessage}
        autoHideDuration={5000}
        onClose={() => setShowMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowMessage(false)} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </Paper>
  );
});

export default PractitionerWeekView;